import TokenService from "./token.service";

const STORAGE_PREFIX = "STOCK_ACCESS_";

/**
 * Keeps the token the API hands out once the stock passcode is accepted, so a
 * reload inside the hour does not ask for the passcode again. One entry per
 * branch - a passcode only unlocks the branch it belongs to.
 */
class StockAccessService {
  storageKey(merchantId, branchKey) {
    return `${STORAGE_PREFIX}${merchantId}_${branchKey}`;
  }

  get(merchantId, branchKey) {
    const access = TokenService.getStockAccess(
      this.storageKey(merchantId, branchKey)
    );

    if (!access?.token || !access?.expiresAt) {
      return null;
    }

    if (access.expiresAt * 1000 <= Date.now()) {
      this.clear(merchantId, branchKey);
      return null;
    }

    return access;
  }

  getToken(merchantId, branchKey) {
    return this.get(merchantId, branchKey)?.token ?? null;
  }

  // `data` is what check-passcode-stock-page returns: token + expires_at.
  save(merchantId, branchKey, data) {
    const access = {
      token: data?.token,
      expiresAt: data?.expires_at,
    };

    if (!access.token || !access.expiresAt) {
      return null;
    }

    TokenService.setStockAccess(this.storageKey(merchantId, branchKey), access);

    return access;
  }

  clear(merchantId, branchKey) {
    TokenService.removeStockAccess(this.storageKey(merchantId, branchKey));
  }

  // The stock endpoints name their parameters in camel case and the order and
  // KDS ones in snake case; both spellings point at the same branch.
  merchantIdOf(dataForm) {
    return dataForm?.merchantId ?? dataForm?.merchant_id ?? "";
  }

  branchKeyOf(dataForm) {
    return (
      dataForm?.branchId ??
      dataForm?.physical_branch_id ??
      dataForm?.branchCode ??
      dataForm?.branch_code ??
      ""
    );
  }

  // Every request the API accepts comes back with a renewed token, so a screen
  // in use keeps sliding its hour forward instead of locking mid shift.
  captureRenewal(response) {
    const token = response?.headers?.["x-stock-token-refresh"];
    const expiresAt = Number(response?.headers?.["x-stock-token-expires-at"]);

    if (!token || !expiresAt) return;

    const dataForm = this.requestFormOf(response.config);
    const merchantId = this.merchantIdOf(dataForm);
    const branchKey = this.branchKeyOf(dataForm);

    if (merchantId === "" || branchKey === "") return;

    this.save(merchantId, branchKey, { token, expires_at: expiresAt });
  }

  // Axios keeps query parameters and the (already serialised) body apart.
  requestFormOf(config) {
    if (!config) return {};
    if (config.params) return config.params;
    if (typeof config.data !== "string") return config.data ?? {};

    try {
      return JSON.parse(config.data);
    } catch (e) {
      return {};
    }
  }

  // Axios config carrying the token for one write on a public stock link.
  requestConfig(dataForm) {
    const token = this.getToken(
      this.merchantIdOf(dataForm),
      this.branchKeyOf(dataForm)
    );

    return token ? { headers: { "X-Stock-Token": token } } : {};
  }
}

export default new StockAccessService();
